import { Alert, DatePicker, Input, message, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { RefreshCcw, Search } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  type AdminCommerceCostChainViewItem,
  adminCommerceStore,
  authStore,
  brandStore,
} from '@/stores';
import type { AdminCommerceCostFilters } from '@/types';
import { formatAdminCommerceCost } from './costFormat';

const { RangePicker } = DatePicker;

function formatInteger(value: number | undefined) {
  return new Intl.NumberFormat('zh-CN').format(value || 0);
}

function formatPercent(value: number | undefined) {
  return `${((value || 0) * 100).toFixed(1)}%`;
}

function rangeFromFilters(filters: AdminCommerceCostFilters): [Dayjs, Dayjs] {
  const end = filters.endAt ? dayjs(filters.endAt) : dayjs();
  return [
    filters.startAt ? dayjs(filters.startAt) : end.subtract(30, 'day'),
    end,
  ];
}

function authSourceLabel(source: string, transport = '') {
  if (source === 'llm_access_token') return 'LAT';
  if (source === 'api_key' && transport === 'bearer') return 'Bearer API Key';
  if (source === 'api_key') return 'X-API-Key';
  if (source === 'session') return 'Session';
  return source === 'legacy_unknown' || !source ? '历史未知' : source;
}

export const AdminCommerceCostDashboardPage = observer(() => {
  const canRead = authStore.can(ADMIN_PERMISSIONS.commerceCostsRead);
  const activeBrand = brandStore.activeBrand;
  const activeAppId = brandStore.activeBrandAppId;
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() =>
    rangeFromFilters(adminCommerceStore.costFilters),
  );

  useEffect(() => {
    const [start, end] = range;
    adminCommerceStore.clearCostDashboard();
    if (!activeAppId || !canRead) return;
    adminCommerceStore.setCostFilters({
      startAt: start.startOf('day').toISOString(),
      endAt: end.endOf('day').toISOString(),
    });
    void adminCommerceStore.loadCostDashboard().catch((error) => {
      console.error('加载成本看板失败:', error);
      message.error('加载成本看板失败');
    });
  }, [range, activeAppId, canRead]);

  const loadDashboard = async () => {
    const [start, end] = range;
    adminCommerceStore.setCostFilters({
      startAt: start.startOf('day').toISOString(),
      endAt: end.endOf('day').toISOString(),
    });
    try {
      await adminCommerceStore.loadCostDashboard();
    } catch (error) {
      console.error('加载成本看板失败:', error);
      message.error('加载成本看板失败');
    }
  };

  const chainColumns = useMemo<Column<AdminCommerceCostChainViewItem>[]>(
    () => [
      {
        id: 'source',
        header: '调用来源',
        accessor: (row) => (
          <span className="inline-flex rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
            {authSourceLabel(row.authSource, row.authTransport)}
          </span>
        ),
      },
      {
        id: 'caller',
        header: '调用方',
        accessor: (row) => (
          <div className="max-w-64">
            <div className="font-medium text-foreground">
              {row.callerName || row.callerId || '-'}
            </div>
            {row.callerName && row.callerId ? (
              <div className="break-all text-xs text-muted-foreground">
                {row.callerId}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'entry',
        header: '入口协议',
        accessor: (row) => row.endpoint || '-',
      },
      {
        id: 'providerRoute',
        header: 'Provider / Route',
        accessor: (row) => (
          <div>
            <div className="font-medium text-foreground">
              {row.provider || '-'}
            </div>
            <div className="text-xs text-muted-foreground">
              {row.routeId || '-'}
            </div>
          </div>
        ),
      },
      {
        id: 'modelChain',
        header: '公开模型 → 上游模型',
        accessor: (row) => (
          <div className="max-w-72">
            <div className="font-medium text-foreground">
              {row.publicModel || '-'}
            </div>
            <div className="break-all text-xs text-muted-foreground">
              {row.upstreamModel || '-'}
            </div>
          </div>
        ),
      },
      {
        id: 'totalCost',
        header: '成本',
        accessor: (row) => formatAdminCommerceCost(row.totalCost),
      },
      {
        id: 'cacheReadRate',
        header: '缓存利用率',
        accessor: (row) => formatPercent(row.cacheReadRate),
      },
      {
        id: 'cacheTokens',
        header: '缓存读 / 有效输入 Tokens',
        accessor: (row) =>
          `${formatInteger(row.cacheReadTokens)} / ${formatInteger(
            row.cacheEligibleTokens ??
              Math.max(
                row.inputTokens,
                row.cacheReadTokens + row.cacheWriteTokens,
              ),
          )}`,
      },
      {
        id: 'requestCount',
        header: '请求数',
        accessor: (row) => formatInteger(row.requestCount),
      },
    ],
    [],
  );

  const summary = adminCommerceStore.costSummary;
  const filters = adminCommerceStore.costFilters;
  const dimensions = adminCommerceStore.costDimensions;
  const trend = adminCommerceStore.costTrendChart;
  const costBreakdown = [
    {
      key: 'input',
      label: '输入',
      cost: summary?.inputCost,
      tokens: summary?.inputTokens,
    },
    {
      key: 'output',
      label: '输出',
      cost: summary?.outputCost,
      tokens: summary?.outputTokens,
    },
    {
      key: 'cacheRead',
      label: '缓存读',
      cost: summary?.cacheReadCost,
      tokens: summary?.cacheReadTokens,
    },
    {
      key: 'cacheWrite',
      label: '缓存写',
      cost: summary?.cacheWriteCost,
      tokens: summary?.cacheWriteTokens,
    },
    { key: 'other', label: '其他', cost: summary?.otherCost },
  ];

  if (!canRead) {
    return (
      <div className="p-6">
        <Alert
          type="warning"
          message="当前账号没有查看成本看板的权限"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="space-y-6 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-lg font-semibold text-foreground">成本看板</h1>
            <span className="rounded bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {activeBrand?.name || '未选择 Brand'}
            </span>
          </div>
          <Button
            variant="outline"
            loading={adminCommerceStore.costLoading}
            onClick={() => void loadDashboard()}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            刷新
          </Button>
        </div>

        <div className="grid gap-3 lg:grid-cols-3 xl:grid-cols-4">
          <RangePicker
            value={range}
            onChange={(value) =>
              value?.[0] && value[1] && setRange([value[0], value[1]])
            }
          />
          <Select
            value={filters.interval || 'day'}
            onChange={(interval) =>
              adminCommerceStore.setCostFilters({ interval })
            }
            options={[
              { value: 'day', label: '按天' },
              { value: 'week', label: '按周' },
              { value: 'month', label: '按月' },
            ]}
          />
          <Select
            value={filters.groupBy || 'providerModel'}
            onChange={(groupBy) =>
              adminCommerceStore.setCostFilters({ groupBy })
            }
            options={[
              { value: 'providerModel', label: 'Provider / 模型' },
              { value: 'provider', label: 'Provider' },
              { value: 'model', label: '模型' },
              { value: 'authSource', label: '调用来源' },
              { value: 'caller', label: '调用方' },
            ]}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder="Provider（可多选）"
            value={filters.providers}
            onChange={(providers) =>
              adminCommerceStore.setCostFilters({ providers })
            }
            options={dimensions.providers.map((value) => ({
              value,
              label: value,
            }))}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder="Model（可多选）"
            value={filters.models}
            onChange={(models) => adminCommerceStore.setCostFilters({ models })}
            options={dimensions.models.map((value) => ({
              value,
              label: value,
            }))}
          />
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="调用来源（可多选）"
            value={filters.authSources}
            onChange={(authSources) =>
              adminCommerceStore.setCostFilters({ authSources })
            }
            options={dimensions.authSources.map((value) => ({
              value,
              label: authSourceLabel(value),
            }))}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            placeholder="调用方（可多选）"
            value={filters.callerIds}
            onChange={(callerIds) =>
              adminCommerceStore.setCostFilters({ callerIds })
            }
            options={dimensions.callers.map((caller) => ({
              value: caller.id,
              label: caller.name || caller.id,
            }))}
          />
          <Input
            placeholder="User ID"
            value={filters.userId}
            onChange={(event) =>
              adminCommerceStore.setCostFilters({ userId: event.target.value })
            }
          />
          <Input
            placeholder="Request ID"
            value={filters.requestId}
            onChange={(event) =>
              adminCommerceStore.setCostFilters({
                requestId: event.target.value,
              })
            }
          />
          <Input
            placeholder="Turn ID"
            value={filters.turnId}
            onChange={(event) =>
              adminCommerceStore.setCostFilters({ turnId: event.target.value })
            }
          />
          <Button
            loading={adminCommerceStore.costLoading}
            onClick={() => void loadDashboard()}
          >
            <Search className="mr-2 h-4 w-4" />
            查询
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Metric
            label="总成本"
            value={formatAdminCommerceCost(summary?.totalCost)}
          />
          <Metric label="请求数" value={formatInteger(summary?.requestCount)} />
          <Metric
            label="总 Tokens"
            value={formatInteger(summary?.totalTokens)}
          />
          <Metric
            label="缓存利用率（Token 加权）"
            value={formatPercent(summary?.cacheReadRate)}
          />
          <Metric label="币种" value={summary?.currency || '-'} />
        </div>

        <section>
          <h2 className="text-sm font-semibold text-foreground">成本构成</h2>
          <div className="mt-3 overflow-x-auto border-y border-border">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">类型</th>
                  <th className="px-3 py-2 text-right font-medium">成本</th>
                  <th className="px-3 py-2 text-right font-medium">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {costBreakdown.map((item) => (
                  <tr
                    key={item.key}
                    className="border-t border-border first:border-t-0"
                  >
                    <td className="px-3 py-2">{item.label}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatAdminCommerceCost(item.cost)}
                    </td>
                    <td className="px-3 py-2 text-right text-muted-foreground">
                      {item.tokens === undefined
                        ? '-'
                        : formatInteger(item.tokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid gap-4 xl:grid-cols-2">
          <TrendChart
            title="成本趋势"
            data={trend.data}
            lines={trend.lines}
            valueKey="key"
            formatter={(value) => formatAdminCommerceCost(value)}
          />
          <TrendChart
            title="Tokens 趋势"
            data={trend.data}
            lines={trend.lines}
            valueKey="tokenKey"
            formatter={formatInteger}
          />
          <TrendChart
            className="xl:col-span-2"
            title="缓存利用率趋势"
            data={trend.data}
            lines={trend.lines}
            valueKey="cacheRateKey"
            formatter={formatPercent}
            percentage
          />
        </div>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">调用链路</h2>
          <DataTable
            columns={chainColumns}
            data={adminCommerceStore.costChains}
            loading={adminCommerceStore.costLoading}
            emptyMessage="暂无调用链路数据"
          />
        </section>
      </div>
    </div>
  );
});

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-2 break-words text-lg font-semibold text-foreground">
        {value}
      </div>
    </div>
  );
}

function TrendChart({
  className = '',
  title,
  data,
  lines,
  valueKey,
  formatter,
  percentage = false,
}: {
  className?: string;
  title: string;
  data: Array<Record<string, string | number>>;
  lines: Array<{
    key: string;
    tokenKey: string;
    cacheRateKey: string;
    label: string;
    color: string;
  }>;
  valueKey: 'key' | 'tokenKey' | 'cacheRateKey';
  formatter: (value: number) => string;
  percentage?: boolean;
}) {
  return (
    <div className={`rounded-lg border border-border p-4 ${className}`}>
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <div className="mt-4 h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="bucketAt"
              tick={{ fontSize: 12, fill: '#64748b' }}
            />
            <YAxis
              width={76}
              domain={percentage ? [0, 1] : undefined}
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickFormatter={(value) => formatter(Number(value))}
            />
            <Tooltip formatter={(value) => formatter(Number(value))} />
            <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
            {lines.map((line) => (
              <Line
                key={line[valueKey]}
                type="monotone"
                dataKey={line[valueKey]}
                name={line.label}
                stroke={line.color}
                strokeWidth={2.25}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
